import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CampaignTable } from '../components/CampaignTable';
import { FilterBar } from '../components/FilterBar';
import ContextMenu from '../components/ContextMenu';
import { apiClient } from '../lib/apiClient';
import type { Campaign, CampaignFilters } from '../types/campaign';

// Toast notification helper
function showToast(message: string, type: 'success' | 'error' = 'success') {
  // Simple toast implementation - in production, use a toast library
  const toast = document.createElement('div');
  toast.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300 ${
    type === 'success' ? 'bg-green-500' : 'bg-red-500'
  } text-white`;
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

export default function CampaignDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  
  // Context Menu State (E007-S005)
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    campaign: Campaign;
  } | null>(null);
  
  const [filters, setFilters] = useState<CampaignFilters>({
    platform: null,
    status: null,
    dateFrom: null,
    dateTo: null,
    minSpend: null,
    maxSpend: null,
    funnelStage: null,
    healthCategories: [],
  });
  
  // Initialize sort state from URL params (AC4: URL persistence)
  const [sortBy, setSortBy] = useState<string>(() => {
    const urlSort = searchParams.get('sort');
    if (urlSort) {
      const parts = urlSort.split('_');
      if (parts.length >= 1) return parts[0];
    }
    return 'spend'; // Default sort
  });
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => {
    const urlSort = searchParams.get('sort');
    if (urlSort) {
      const parts = urlSort.split('_');
      if (parts.length >= 2) return parts[1] as 'asc' | 'desc';
    }
    return 'desc';
  });
  // Track sort click count for 3rd-click clear (AC3)
  const [sortClickCount, setSortClickCount] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch campaigns from API
  useEffect(() => {
    const fetchCampaigns = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await apiClient.get('/analytics/campaigns', {
          params: {
            platform: filters.platform,
            status: filters.status,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            page_size: itemsPerPage,
          },
        });
        
        setCampaigns(response.data.campaigns || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load campaigns');
        console.error('Error fetching campaigns:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCampaigns();
  }, [filters, sortBy, sortDir, page, itemsPerPage]);

  // Filter campaigns by search term (client-side)
  const filteredCampaigns = useMemo(() => {
    if (!searchTerm) return campaigns;
    
    return campaigns.filter((campaign) =>
      campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.platform.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.objective?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [campaigns, searchTerm]);

  // AC3: 3rd-click to clear sort - cycle through desc → asc → default
  // AC4: Persist sort state in URL
  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Same column clicked
      if (sortDir === 'desc') {
        // 2nd click: switch to asc
        setSortDir('asc');
        setSortClickCount(2);
        setSearchParams(new URLSearchParams({ sort: `${column}_asc` }));
      } else {
        // 3rd click (asc): clear sort, return to default
        setSortBy('spend');
        setSortDir('desc');
        setSortClickCount(0);
        setSearchParams(new URLSearchParams()); // Clear URL param
      }
    } else {
      // New column clicked - start with desc
      setSortBy(column);
      setSortDir('desc');
      setSortClickCount(1);
      setSearchParams(new URLSearchParams({ sort: `${column}_desc` }));
    }
  };

  const handleFilterChange = (newFilters: CampaignFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  const handleFilterReset = () => {
    setFilters({
      platform: null,
      status: null,
      dateFrom: null,
      dateTo: null,
      minSpend: null,
      maxSpend: null,
      funnelStage: null,
      healthCategories: [],
    });
    setSearchTerm('');
    setPage(1);
  };

  // E007-S005: Context Menu Handlers
  const handleContextMenu = useCallback((event: React.MouseEvent, campaign: Campaign) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      campaign,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleContextMenuAction = useCallback(async (
    action: string,
    campaign: Campaign,
    data?: { boardId?: string; boardName?: string }
  ) => {
    try {
      switch (action) {
        case 'pause':
          // Optimistic update
          setCampaigns((prev) =>
            prev.map((c) =>
              c.unified_id === campaign.unified_id ? { ...c, status: 'paused' as const } : c
            )
          );
          await apiClient.post(`/actions/campaigns/${campaign.unified_id}/pause`);
          showToast(`Paused: ${campaign.campaign_name}`);
          break;

        case 'activate':
          // Optimistic update
          setCampaigns((prev) =>
            prev.map((c) =>
              c.unified_id === campaign.unified_id ? { ...c, status: 'active' as const } : c
            )
          );
          await apiClient.post(`/actions/campaigns/${campaign.unified_id}/activate`);
          showToast(`Activated: ${campaign.campaign_name}`);
          break;

        case 'add-to-board':
          if (data?.boardId) {
            await apiClient.post(`/boards/${data.boardId}/cards`, {
              title: campaign.campaign_name,
              campaign_id: campaign.unified_id,
              position: 1000, // Add to end
            });
            showToast(`Added to board: ${data.boardName}`);
          }
          break;

        case 'export': {
          // Trigger export for single campaign
          const exportData = JSON.stringify(campaign, null, 2);
          const blob = new Blob([exportData], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${campaign.campaign_name.replace(/\s+/g, '_')}_export.json`;
          a.click();
          URL.revokeObjectURL(url);
          showToast('Export downloaded');
          break;
        }

        case 'duplicate':
          showToast('Duplicate feature coming soon', 'success');
          break;

        default:
          console.warn('Unknown action:', action);
      }
    } catch (err) {
      console.error('Action failed:', err);
      showToast('Action failed. Please try again.', 'error');
      // Refresh campaigns on error
      window.location.reload();
    }
  }, []);

  const totalItems = filteredCampaigns.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md p-6 text-center bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <div className="mb-4 text-red-500">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">Error Loading Campaigns</h3>
          <p className="mb-4 text-gray-600 dark:text-gray-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Campaign Overview
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            {totalItems} campaigns across Meta, Google Ads, and TikTok
          </p>
        </header>

        {/* Filters */}
        <FilterBar
          filters={filters}
          onChange={handleFilterChange}
          onReset={handleFilterReset}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {/* Campaign Table */}
        <CampaignTable
          campaigns={filteredCampaigns}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={handleSort}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onContextMenu={handleContextMenu}
        />
        
        {/* E007-S005: Context Menu */}
        {contextMenu && (
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            campaign={contextMenu.campaign}
            onClose={handleCloseContextMenu}
            onAction={handleContextMenuAction}
          />
        )}

        {/* Empty State */}
        {filteredCampaigns.length === 0 && (
          <div className="py-12 text-center bg-white rounded-lg shadow dark:bg-gray-800">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
              No campaigns found
            </h3>
            <p className="mb-4 text-gray-500 dark:text-gray-400">
              {searchTerm || filters.platform || filters.status
                ? 'Try adjusting your filters'
                : 'Get started by connecting your advertising accounts'}
            </p>
            {(searchTerm || filters.platform || filters.status) && (
              <button
                onClick={handleFilterReset}
                className="px-4 py-2 text-white bg-blue-500 rounded hover:bg-blue-600"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
